/**
 * Helpers for rendering customizationPlacements changes in Campaign Logs.
 * Groups items by Meta placement category: Feeds, Reels/Stories, Audience Network.
 */

export const GROUP_FEEDS = "feeds";
export const GROUP_REELS_STORIES = "reelsAndStories";
export const GROUP_AUDIENCE_NETWORK = "audienceNetwork";

const REELS_STORIES_POSITIONS = [
  "story",
  "reels",
  "profile_reels",
  "facebook_reels",
];

/**
 * Derives the visible group for a customizationPlacement item from its positions.
 * Priority: Reels/Stories (if story/reels) > Audience Network (if AN positions) > Feeds.
 *
 * @param {Object} item - customizationPlacement item with facebookPositions, instagramPositions, etc.
 * @returns {string} GROUP_FEEDS | GROUP_REELS_STORIES | GROUP_AUDIENCE_NETWORK
 */
export const getCustomizationPlacementGroup = (item) => {
  if (!item || typeof item !== "object") return GROUP_FEEDS;

  const allPositions = [
    ...(item.facebookPositions || []),
    ...(item.instagramPositions || []),
    ...(item.messengerPositions || []),
    ...(item.audienceNetworkPositions || []),
  ];

  const hasReelsStories = REELS_STORIES_POSITIONS.some((p) =>
    allPositions.includes(p)
  );
  if (hasReelsStories) return GROUP_REELS_STORIES;

  const hasAudienceNetwork = (item.audienceNetworkPositions || []).length > 0;
  if (hasAudienceNetwork) return GROUP_AUDIENCE_NETWORK;

  return GROUP_FEEDS;
};

/**
 * Extracts customizationPlacements from mediaData (first item).
 *
 * @param {Array} mediaData - mediaData array from ad
 * @returns {Array} customizationPlacements or []
 */
export const extractCustomizationPlacements = (mediaData) => {
  if (!Array.isArray(mediaData) || !mediaData[0]) return [];
  return mediaData[0]?.customizationPlacements || [];
};

/**
 * Groups customizationPlacements by derived group.
 * Each group maps to the first item in that group (for display).
 *
 * @param {Array} customizationPlacements
 * @returns {Object} { [groupKey]: item }
 */
export const groupCustomizationPlacementsByCategory = (
  customizationPlacements
) => {
  const byGroup = {};
  for (const item of customizationPlacements || []) {
    const group = getCustomizationPlacementGroup(item);
    if (!byGroup[group]) byGroup[group] = item;
  }
  return byGroup;
};

/**
 * Computes diff between old and new customizationPlacements for log display.
 *
 * @param {Array} oldPlacements - old customizationPlacements
 * @param {Array} newPlacements - new customizationPlacements
 * @returns {{ added: string[], removed: string[], changed: Array<{ group: string, oldItem: Object, newItem: Object }> }}
 */
export const diffCustomizationPlacements = (oldPlacements, newPlacements) => {
  const oldByGroup = groupCustomizationPlacementsByCategory(oldPlacements);
  const newByGroup = groupCustomizationPlacementsByCategory(newPlacements);

  const allGroups = new Set([
    ...Object.keys(oldByGroup),
    ...Object.keys(newByGroup),
  ]);

  const added = [];
  const removed = [];
  const changed = [];

  for (const group of allGroups) {
    const oldItem = oldByGroup[group];
    const newItem = newByGroup[group];

    if (!oldItem && newItem) added.push(group);
    else if (oldItem && !newItem) removed.push(group);
    else if (oldItem && newItem) {
      const oldUrl = oldItem.mediaUrl || oldItem.preview || "";
      const newUrl = newItem.mediaUrl || newItem.preview || "";
      if (oldUrl !== newUrl) {
        changed.push({ group, oldItem, newItem });
      }
    }
  }

  return { added, removed, changed };
};
